<script setup lang="ts">
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { useTea } from "../lib/tea";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  useTemplateRef,
} from "vue";
import {
  clearCachedEntitlements,
  clearCloudSession,
  fetchEntitlements,
  getCloudSession,
  hasActiveEntitlement,
  requestDesktopMagicLink,
  writeCachedEntitlements,
  type CloudEntitlement,
  type CloudSession,
} from "../lib/cloudAuth";
import { CLOUD_AUTH_EVENT } from "../lib/cloudAuthDeepLink";
import {
  activateDesktopLicenseFromFile,
  clearDesktopLicense,
  getInstalledDesktopLicense,
  syncDesktopLicenseFromCloud,
  type DesktopLicenseInfo,
} from "../lib/desktopLicense";
import { isTauri } from "../lib/runtime";
import {
  initialAccountPanelModel,
  updateAccountPanelFn,
  type AccountPanelCmd,
  type AccountPanelEntitlement,
  type AccountPanelLicense,
  type AccountPanelSession,
} from "./AccountPanel.update";

const emit = defineEmits<{
  close: [];
  "auth-changed": [];
}>();

const props = withDefaults(
  defineProps<{
    /** When true, copy emphasizes unlock (splash gate). */
    requireAccess?: boolean;
  }>(),
  { requireAccess: false },
);

const emailInput = useTemplateRef<HTMLInputElement>("emailInput");

function asPanelSession(session: CloudSession): AccountPanelSession {
  return session;
}

function asPanelEntitlements(
  entitlements: CloudEntitlement[],
): AccountPanelEntitlement[] {
  return entitlements;
}

function asPanelLicense(license: DesktopLicenseInfo): AccountPanelLicense {
  return license;
}

function initialSession(): ReturnType<typeof initialAccountPanelModel> {
  const sessionM = toMaybe(getCloudSession());
  const session: Maybe<AccountPanelSession> = isJust(sessionM)
    ? just(asPanelSession(sessionM.value))
    : nothing;
  return initialAccountPanelModel(session);
}

async function runCmd(cmd: AccountPanelCmd): Promise<void> {
  switch (cmd.kind) {
    case "refreshEntitlements": {
      if (isNothing(model.value.session)) {
        void dispatch({ type: "EntitlementsLoaded", entitlements: [] });
        return;
      }
      try {
        const res = await fetchEntitlements();
        void dispatch({
          type: "EntitlementsLoaded",
          entitlements: asPanelEntitlements(res.entitlements),
        });
        writeCachedEntitlements(res);
        if (
          isTauri() &&
          hasActiveEntitlement(res.entitlements, "desktop_license")
        ) {
          try {
            const synced = toMaybe(await syncDesktopLicenseFromCloud());
            const license: Maybe<AccountPanelLicense> = isJust(synced)
              ? just(asPanelLicense(synced.value))
              : nothing;
            void dispatch({ type: "LicenseLoaded", license });
          } catch {
            // Offline license may already be installed; ignore sync failure.
          }
        }
      } catch (error) {
        void dispatch({
          type: "EntitlementsFailed",
          error:
            error instanceof Error
              ? error.message
              : "We couldn't check your account right now.",
        });
      }
      return;
    }
    case "refreshLocalLicense": {
      if (isTauri() === false) {
        void dispatch({ type: "LicenseLoaded", license: nothing });
        return;
      }
      const licenseM = toMaybe(await getInstalledDesktopLicense());
      const license: Maybe<AccountPanelLicense> = isJust(licenseM)
        ? just(asPanelLicense(licenseM.value))
        : nothing;
      void dispatch({ type: "LicenseLoaded", license });
      return;
    }
    case "requestMagicLink": {
      try {
        const next = await requestDesktopMagicLink(cmd.email);
        void dispatch({
          type: "MagicLinkSucceeded",
          session: asPanelSession(next),
        });
      } catch (error) {
        void dispatch({
          type: "MagicLinkFailed",
          error:
            error instanceof Error
              ? error.message
              : "We couldn't send that link. Try again in a moment.",
        });
      }
      return;
    }
    case "activateLicenseFile": {
      try {
        const license = await activateDesktopLicenseFromFile();
        void dispatch({
          type: "ActivateSucceeded",
          license: asPanelLicense(license),
        });
      } catch (error) {
        void dispatch({
          type: "ActivateFailed",
          error:
            error instanceof Error
              ? error.message
              : "Could not activate that license.",
        });
      }
      return;
    }
    case "clearSessionAndLicense": {
      clearCloudSession();
      clearCachedEntitlements();
      if (isTauri()) {
        await clearDesktopLicense();
      }
      void dispatch({ type: "SignedOut", doneMessage: cmd.doneMessage });
      return;
    }
    case "emitAuthChanged":
      emit("auth-changed");
      return;
    case "emitClose":
      emit("close");
      return;
    case "focusEmail":
      void nextTick(() => emailInput.value?.focus());
      return;
    case "readSession": {
      const nextM = toMaybe(getCloudSession());
      if (isNothing(nextM)) return;
      void dispatch({
        type: "SessionSynced",
        session: asPanelSession(nextM.value),
      });
      return;
    }
    default:
      assertNever(cmd);
  }
}

const { model, dispatch } = useTea(initialSession(), updateAccountPanelFn, {
  runCmd: just(runCmd),
});

const session = computed(() => model.value.session);
const entitlements = computed(
  (): CloudEntitlement[] => model.value.entitlements as CloudEntitlement[],
);
const localLicense = computed(() => model.value.localLicense);
const busy = computed(() => model.value.busy);
const statusTone = computed(() => model.value.statusTone);

const email = computed({
  get: (): string => model.value.email,
  set: (value: string): void => {
    void dispatch({ type: "EmailChanged", email: value });
  },
});

const unlocked = computed(
  () => isJust(session.value) || isJust(localLicense.value),
);
/**
 * A license on disk keeps the app usable while the cloud session is gone
 * (expired, revoked, or dropped on an update). That state still needs the
 * sign-in field, or reconnecting means signing out first.
 */
const needsCloudSignIn = computed(() => isNothing(session.value));

const desktopLicensed = computed(
  () =>
    isJust(localLicense.value) ||
    hasActiveEntitlement(entitlements.value, "desktop_license"),
);
const cloudBackup = computed(() =>
  hasActiveEntitlement(entitlements.value, "cloud_backup"),
);

const writerMessage = computed(() => {
  if (needsCloudSignIn.value) {
    return "This device is activated, but it isn't signed in to the cloud, so backups are paused.";
  }
  if (desktopLicensed.value && cloudBackup.value) {
    return "You're all set. Your manuscripts are backed up.";
  }
  if (desktopLicensed.value) {
    return "You're all set to write on this device.";
  }
  if (cloudBackup.value) {
    return "Your manuscripts are backed up in the cloud.";
  }
  return "Your projects stay on this computer for now.";
});

/** Without a cloud session, signing out only removes the local activation. */
const signOutLabel = computed(() =>
  needsCloudSignIn.value ? "Deactivate this device" : "Sign out",
);

const statusText = computed(() =>
  matchMaybe(model.value.status, {
    just: (message) => message,
    nothing: () => "",
  }),
);

const showStatus = computed(() => {
  if (isNothing(model.value.status)) return false;
  if (unlocked.value === true && statusTone.value === "success") return false;
  return true;
});

function onCloudAuthEvent(): void {
  void dispatch({ type: "CloudAuthEvent" });
}

function onVisible(): void {
  if (document.visibilityState === "visible") {
    void dispatch({ type: "Visibility" });
  }
}

onMounted(() => {
  window.addEventListener(CLOUD_AUTH_EVENT, onCloudAuthEvent);
  document.addEventListener("visibilitychange", onVisible);
  void dispatch({ type: "Mounted" });
});

onUnmounted(() => {
  window.removeEventListener(CLOUD_AUTH_EVENT, onCloudAuthEvent);
  document.removeEventListener("visibilitychange", onVisible);
});
</script>

<template>
  <div class="account-panel">
    <header class="account-header">
      <h2 id="account-title" class="account-title">Account</h2>
      <p v-if="unlocked === false" class="account-lede">
        {{
          props.requireAccess
            ? "Sign in or activate a license to continue."
            : "Sign in for cloud backup."
        }}
      </p>
    </header>

    <div class="account-main">
      <div v-if="unlocked" class="account-signed-in">
        <p class="account-voice">{{ writerMessage }}</p>
      </div>

      <div v-if="needsCloudSignIn" class="account-sign-block">
        <form
          id="account-sign-in-form"
          class="account-sign-in"
          @submit.prevent="dispatch({ type: 'SendLink' })"
        >
          <label class="account-sr-only" for="account-email">Email</label>
          <input
            id="account-email"
            ref="emailInput"
            v-model="email"
            class="account-email-input"
            type="email"
            required
            autocomplete="email"
            writingsuggestions="false"
            placeholder="Your email"
            :disabled="busy"
          />
        </form>
        <button
          v-if="isTauri() && isNothing(localLicense)"
          type="button"
          class="account-action account-license-link"
          :disabled="busy"
          @click="dispatch({ type: 'ActivateFromFile' })"
        >
          Or activate license
        </button>
      </div>

      <p
        v-if="showStatus"
        class="account-status"
        :data-tone="statusTone"
        role="status"
        aria-live="polite"
      >
        <span
          v-if="statusTone === 'waiting'"
          class="account-status-dot"
          aria-hidden="true"
        />
        {{ statusText }}
      </p>
    </div>

    <footer class="account-footer">
      <button
        v-if="unlocked"
        type="button"
        class="account-action account-sign-out"
        :disabled="busy"
        @click="dispatch({ type: 'SignOut' })"
      >
        {{ signOutLabel }}
      </button>
      <button
        v-else
        type="button"
        class="account-action account-cancel"
        :disabled="busy"
        @click="dispatch({ type: 'Close' })"
      >
        Cancel
      </button>

      <button
        v-if="needsCloudSignIn"
        type="submit"
        form="account-sign-in-form"
        class="account-action account-action-primary account-done"
        :disabled="busy"
      >
        {{ busy ? "Waiting…" : "Send link" }}
      </button>
      <button
        v-else
        type="button"
        class="account-action account-action-primary account-done"
        @click="dispatch({ type: 'Close' })"
      >
        Done
      </button>
    </footer>
  </div>
</template>

<style scoped>
.account-panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-lg);
  min-height: 0;
  color: var(--text);
}

.account-header {
  display: grid;
  gap: 0.35rem;
  flex-shrink: 0;
}

.account-title {
  margin: 0;
  font-family: var(--font-heading);
  font-size: clamp(1.75rem, 5vw, 2.1rem);
  font-weight: 400;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: color-mix(in oklch, var(--color-zw-amber) 38%, var(--text-strong));
}

.account-lede {
  margin: 0;
  max-width: 28ch;
  font-family: var(--font-prose);
  font-size: 0.95rem;
  line-height: 1.45;
  color: var(--text-muted);
}

.account-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  gap: var(--space-md);
  min-height: 0;
}

.account-signed-in {
  display: grid;
  gap: var(--space-sm);
  justify-items: start;
}

.account-voice {
  margin: 0;
  max-width: 28ch;
  font-family: var(--font-prose);
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-muted);
}

.account-sign-in {
  display: grid;
  width: 100%;
}

.account-sign-block {
  display: grid;
  gap: 0.45rem;
  width: 100%;
  justify-items: start;
}

.account-email-input {
  appearance: none;
  width: 100%;
  box-sizing: border-box;
  border: 0;
  border-bottom: 1px solid var(--line);
  border-radius: 0;
  background: transparent;
  padding: 0.4rem 0 0.5rem;
  font-family: var(--font-prose);
  font-size: 1.1rem;
  color: var(--text-strong);
  transition: border-color var(--duration-fast) var(--ease-out-quart);
}

.account-email-input::placeholder {
  color: color-mix(in oklch, var(--text-muted) 76%, transparent);
}

.account-email-input:hover:not(:disabled) {
  border-bottom-color: color-mix(in oklch, var(--line) 55%, var(--text-muted));
}

.account-email-input:focus {
  outline: none;
  border-bottom-color: var(--color-zw-amber);
}

.account-email-input:disabled {
  opacity: 0.65;
}

.account-action {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0.15rem 0;
  font-family: var(--font-ui);
  font-size: 1.05rem;
  line-height: 1.4;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.15rem;
  transition: color var(--duration-fast) var(--ease-out-quart);
}

.account-action-primary {
  font-size: 1.2rem;
  color: var(--text-strong);
}

.account-action:hover:not(:disabled) {
  color: var(--text-strong);
}

.account-action:disabled {
  opacity: 0.55;
  cursor: default;
}

.account-action:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 3px;
}

.account-status {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  margin: 0;
  max-width: 30ch;
  font-family: var(--font-prose);
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--text-muted);
}

.account-status[data-tone="success"] {
  color: var(--text-strong);
}

.account-status[data-tone="error"] {
  color: var(--danger);
}

.account-status-dot {
  flex-shrink: 0;
  width: 0.45rem;
  height: 0.45rem;
  margin-top: 0.45rem;
  border-radius: 999px;
  background: var(--color-zw-amber);
}

.account-footer {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  flex-shrink: 0;
  margin-top: auto;
  padding: var(--space-md) 0;
}

.account-footer::before {
  content: "";
  position: absolute;
  top: 0;
  left: calc(-1 * var(--zw-flyout-inset, var(--space-lg)));
  right: calc(-1 * var(--zw-flyout-inset, var(--space-lg)));
  height: 1px;
  background: var(--line);
}

.account-footer .account-action {
  padding: 0.35rem 0.25rem;
}

.account-done {
  margin-left: auto;
}

.account-cancel,
.account-sign-out,
.account-license-link {
  font-size: 0.82rem;
  letter-spacing: 0.01em;
  color: color-mix(in oklch, var(--text-muted) 82%, transparent);
}

.account-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .account-status-dot {
    animation: account-pulse 1.6s var(--ease-out-quart) infinite;
  }
}

@keyframes account-pulse {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}
</style>
